import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useLocation, useParams } from 'react-router';
import {
  Grid,
  Typography,
  CircularProgress,
  Link,
  useTheme,
} from '@mui/material';
import RoomIcon from '@mui/icons-material/Room';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import parse from 'html-react-parser';
import { useSelector } from 'react-redux';

import { getPlateById } from '@shared/services/services';
import TabsHeader from '@shared/components/TabsHeader';
import Navigate from '@shared/components/Navigate/navigate';
import PlateIcons from '@shared/components/PlateIcons';

import ImageHeader from './ImageHeader';
import PlateNotFound from './PlateNotFound';
import PlateForm from './PlateForm';

import { submitPlate } from './submitPlate';
import { initialValues, initialValuesCCO } from './initialValues';
import { validationSchema, validationSchemaCCO } from './validationSchema';
import { arrayMonths } from './arrayMonths';
import { typeMap } from './typeMap';
import { getPlateAmount } from '@shared/helpers/getPlateAmount';
import { FLIGHTS } from '@shared/helpers/routes';
import { ID_SITE } from '@shared/conf/env';
import useWindowSize from '@shared/hooks/useWindowsSize';

import {
  RootContainer,
  LegalContainer,
  FilterButtonUpContainer,
  ButtonUpMobile,
  IconContainer,
  MonthsContainer,
  TagTitle,
  TagSubtitle,
  BorderedBoxStyle,
} from './styles';
import { InfoDialogModal } from '@shared/components/Dialogs';
import { usePayeeSelection } from '@shared/components/PayeeSelector/usePayeeSelection';

const Plates = () => {
  const { plateID } = useParams();
  const { pathname, search, hash } = useLocation();

  const plate_link = `${window.location.origin}${pathname || ''}${
    search || ''
  }${hash || ''}`;

  const ID_SITE_CONST = Number(ID_SITE);

  const { pageConfiguration, user } = useSelector(store => ({
    pageConfiguration: store.pageConfiguration,
    user: store.userInfo,
  }));

  const theme = useTheme();

  const { name } = pageConfiguration;

  const [plate, setPlate] = useState(null);
  const [isPlateLoading, setIsPlateLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [titleModal, setTitleModal] = useState('');
  const [subtitleModal, setSubtitleModal] = useState('');
  const [scrollY, setScrollY] = useState(0);
  const [minorsArray, setMinorsArray] = useState([]);

  const screenSize = useWindowSize();

  const payeeState = usePayeeSelection({ forcePointOfSale: true });

  const payee = {
    ...payeeState,
    isPointOfSale: true,
    mainSellers: [],
    seller: null,
    handleSelectSeller: () => {},
  };

  const queryParams = new URLSearchParams(search);
  const type = queryParams.get('type');

  const formik = useFormik({
    initialValues: ID_SITE_CONST === 3 ? initialValuesCCO : initialValues,
    validationSchema:
      ID_SITE_CONST === 3 ? validationSchemaCCO : validationSchema,
    onSubmit: submitPlate(
      plate_link,
      plateID,
      pageConfiguration,
      plate,
      user,
      setTitleModal,
      setSubtitleModal,
      setShowModal,
      payee.handleSelectSubsidiary
    ),
  });

  useEffect(() => {
    setMinorsArray(
      Array.from({ length: formik.values.minors_quantity }, (_, i) => ({
        ind: i,
        age: 0,
        quantity: 0,
      }))
    );
  }, [formik.values.minors_quantity]);

  const changeYear = (menYear, menId) => {
    const updated = minorsArray.map(m =>
      m.ind === menId ? { ...m, year: menYear } : m
    );
    setMinorsArray(updated);
  };

  // Fetch plate y subsidiaries
  useEffect(() => {
    const fetchPlate = async () => {
      try {
        setIsPlateLoading(true);
        const result = await getPlateById(plateID);
        const monthsInOrder = result.months.sort((a, b) => a - b);
        setPlate({ ...result, months: monthsInOrder });
        formik.setFieldValue('plate_title', result.title);
        formik.setFieldValue('sites', result.sites.id);
      } catch (e) {
        console.error(e);
      } finally {
        setIsPlateLoading(false);
      }
    };

    fetchPlate();
  }, [plateID]);

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToTop = () => window.scrollTo(0, 0);

  if (isPlateLoading) {
    return (
      <Grid container justifyContent="center" sx={{ m: 5 }}>
        <CircularProgress size={100} />
      </Grid>
    );
  }

  if (!plate) {
    return <PlateNotFound />;
  }

  return (
    <RootContainer>
      {screenSize.width < 1280 && scrollY > 750 && (
        <FilterButtonUpContainer>
          <ButtonUpMobile variant="contained" onClick={handleScrollToTop}>
            <ExpandLessIcon />
          </ButtonUpMobile>
        </FilterButtonUpContainer>
      )}

      <InfoDialogModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={titleModal}
        text={subtitleModal}
      />
      <TabsHeader site={+type} />
      <Grid
        item
        xs={12}
        className="minWebPadding"
        sx={{
          pb: 1,
          paddingTop: '10px',
          paddingBottom: '10px',
        }}
      >
        <Navigate
          steps={[
            { title: 'Inicio', navigate: FLIGHTS },
            { title: typeMap[+type] || '', navigate: null },
          ]}
        />
      </Grid>

      <Grid
        container
        data-testid="placa_container_banner"
        className="minWebPadding"
        sx={{
          py: { xs: 2, sm: 5 },
          backgroundColor: '#F6F6F6',
        }}
        style={{ paddingTop: '0px' }}
      >
        <BorderedBoxStyle container>
          <Grid item sm={9} xs={12}>
            <ImageHeader image={plate.image_banner} detail={false} />
          </Grid>

          <Grid
            item
            sm={3}
            xs={12}
            sx={{
              paddingLeft: '40px',
              '@media (max-width:600px)': {
                paddingLeft: '0px',
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 22,
                lineHeight: 1.1,
                color: '#000',
                paddingBottom: '15px',
                '@media (max-width:600px)': {
                  paddingTop: '20px',
                },
              }}
            >
              {plate.title}
            </Typography>

            <Grid>
              <TagTitle>Categoría / Región</TagTitle>
              <IconContainer
                alignItems="center"
                sx={{
                  backgroundColor:
                    ID_SITE_CONST === 1 ? theme.primary : theme.headerColor,
                }}
              >
                <RoomIcon
                  sx={{
                    color: '#000000ff',
                    mr: 1,
                    height: '20px',
                    width: '20px',
                  }}
                />
                <TagSubtitle>{plate.categories?.name || ''}</TagSubtitle>
              </IconContainer>
            </Grid>

            <Grid item>
              <TagTitle>Tipo</TagTitle>
              <IconContainer
                alignItems="center"
                sx={{
                  backgroundColor:
                    ID_SITE_CONST === 1 ? theme.primary : theme.headerColor,
                }}
              >
                <PlateIcons
                  sx={{ height: '20px', width: '20px' }}
                  icon={plate.transfers?.icon}
                />
                <TagSubtitle>{plate.transfers?.name || ' '}</TagSubtitle>
              </IconContainer>
            </Grid>

            <Grid item>
              <TagTitle>{plate.months?.length > 1 ? 'Meses' : 'Mes'}</TagTitle>
              <MonthsContainer
                alignItems="center"
                sx={{
                  backgroundColor:
                    ID_SITE_CONST === 1 ? theme.primary : theme.headerColor,
                }}
              >
                <DateRangeIcon
                  sx={{
                    color: '#000000ff',
                    mr: 1,
                    height: '20px',
                    width: '20px',
                  }}
                />
                {plate.months?.length > 0 ? (
                  plate.months.map((mes, key) => (
                    <TagSubtitle key={key}>
                      {arrayMonths[mes - 1]}
                      {key < plate.months.length - 1 ? ',' : ''}
                    </TagSubtitle>
                  ))
                ) : (
                  <TagSubtitle>Consultar Meses</TagSubtitle>
                )}
              </MonthsContainer>
            </Grid>

            <Grid
              container
              sx={{
                flexDirection: 'column',
                alignItems: { xs: 'flex-start' },
                mt: { xs: 2, sm: 0 },
              }}
              style={{ marginTop: '25px' }}
            >
              {plate.from && (
                <Typography
                  sx={{ fontSize: 18, color: '#000000ff', fontWeight: 600 }}
                >
                  Desde
                </Typography>
              )}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 32,
                  color: '#c43131ff',
                }}
              >
                {getPlateAmount({
                  amount: plate.amount,
                  money_type: plate.money_type,
                })}
              </Typography>
              {plate.taxes && (
                <Typography
                  sx={{
                    fontSize: 18,
                    color: '#000000ff',
                    mt: 1,
                    marginTop: '0px',
                  }}
                >
                  Impuestos{' '}
                  <span style={{ fontWeight: 600 }}>
                    {getPlateAmount({
                      amount: plate.taxes,
                      money_type: plate.money_type,
                    })}
                  </span>
                </Typography>
              )}
            </Grid>
          </Grid>
        </BorderedBoxStyle>
        <BorderedBoxStyle item xs={12}>
          <Grid
            container
            justifyContent="space-between"
            sx={{
              mt: 3,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
            style={{ marginTop: '0px' }}
          >
            <Grid item xs={12} sm={9}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 20, sm: 30 },
                  lineHeight: 1.1,
                  color: '#000',
                }}
              >
                {plate.title}
              </Typography>
            </Grid>
          </Grid>

          <Typography
            component={'span'}
            sx={{
              display: 'block',
              mt: 3,
              color: '#000',
              '& p': { marginBottom: '10px' },
            }}
          >
            {parse(plate.elements)}
          </Typography>

          {plate.pdf_link && (
            <Link
              href={`${plate.pdf_link}?v=${Date.now()}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-block',
                my: 4,
                fontWeight: 600,
                color: theme.tertiary,
              }}
            >
              Descargar documento
            </Link>
          )}

          {/*</Paper>*/}
        </BorderedBoxStyle>

        {ID_SITE_CONST !== 2 && (
          <PlateForm
            formik={formik}
            minorsArray={minorsArray}
            changeYear={changeYear}
            ID_SITE_CONST={ID_SITE_CONST}
            subsidiaries={payee.subsidiaries}
            subsidiary={payee.subsidiary ?? {}}
            handleSubsidiarie={payee.handleSelectSubsidiary}
            screenSize={screenSize}
            name={name}
            payee={payee}
          />
        )}

        <LegalContainer>
          <Typography className="title">Legales</Typography>
          <Typography className="text">{plate.legal}</Typography>
        </LegalContainer>
      </Grid>
    </RootContainer>
  );
};

export default Plates;
